import { useStore } from '@nanostores/react';
import { motion, type HTMLMotionProps, type Variants } from 'framer-motion';
import { memo, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  type OnChangeCallback as OnEditorChange,
  type OnScrollCallback as OnEditorScroll,
} from '~/components/editor/codemirror/CodeMirrorEditor';
import { workbenchStore } from '~/lib/stores/workbench';
import { classNames } from '~/utils/classNames';
import { cubicEasingFn } from '~/utils/easings';
import { renderLogger } from '~/utils/logger';
import { EditorPanel } from './EditorPanel';
import { Preview } from './Preview';
import { ErrorModal } from '~/components/ui/ErrorModal';
import { useNavigate } from '@remix-run/react';
import { useMessageParser } from '~/lib/hooks';
import type { StoreFileAction } from '~/types/actions';
import * as Sentry from '@sentry/remix';
import { userStore } from '~/lib/stores/user';
interface WorkspaceProps {
  chatStarted?: boolean;
  isStreaming?: boolean;
  isFlutter?: boolean;
  storeFileActions?: (fileActions: StoreFileAction[]) => Promise<void>;
}

const viewTransition = { ease: cubicEasingFn };

/*
 * const sliderOptions: SliderOptions<WorkbenchViewType> = {
 *   left: {
 *     value: 'code',
 *     text: 'Code',
 *   },
 *   right: {
 *     value: 'preview',
 *     text: 'Preview',
 *   },
 * };
 */

const workbenchVariants = {
  closed: {
    width: 0,
    transition: {
      duration: 0.2,
      fadeOut: 0.4,
    },
  },
  open: {
    width: 'var(--workbench-width)',
    transition: {
      duration: 0.2,
      fadeIn: 0.4,
    },
  },
} satisfies Variants;

export const Workbench = memo(({ chatStarted, isStreaming, isFlutter = false, storeFileActions }: WorkspaceProps) => {
  const [url, setUrl] = useState('');
  const [dartPadisLoading, setDartPadisLoading] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const { email } = userStore.get();

  // const { initialMessages, initialFileActions, storeMessageHistory } = useChatHistory();
  const { fileActions } = useMessageParser();

  // console.log('[refresh] initialFileActions from workbench.client:', initialFileActions);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const navigate = useNavigate();
  renderLogger.trace('Workbench');

  // const hasPreview = useStore(computed(workbenchStore.previews, (previews) => previews.length > 0));
  const showWorkbench = useStore(workbenchStore.showWorkbench);
  const selectedFile = useStore(workbenchStore.selectedFile);
  const currentDocument = useStore(workbenchStore.currentDocument);
  const unsavedFiles = useStore(workbenchStore.unsavedFiles);
  const files = useStore(workbenchStore.files);
  const selectedView = useStore(workbenchStore.currentView);
  const deviceView = useStore(workbenchStore.deviceView);

  const code = localStorage.getItem('flutterCode');
  useEffect(() => {
    // console.warn('[flutter] isFlutter from workbench: ', isFlutter);
    setDartPadisLoading(true);

    if (isFlutter && code && location.pathname !== '/') {
      console.log('workbench flutter code:', code);

      const generateDartPadUrl = async () => {
        try {
          await Sentry.startSpan(
            {
              name: 'fetchFlutterPreview',
              op: 'fetchFlutterPreview',
            },
            async () => {
              const response = await fetch(`/flutter/preview`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ code }),
              });
              console.log('response from flutter/preview:', response);

              const data: any = await response.json();
              setUrl(data.url);
              setDartPadisLoading(false);
            },
          );
        } catch (error) {
          console.error('Error generating DartPad URL:', error);
          setDartPadisLoading(false);
          setShowErrorModal(true);

          throw error;
        }
      };
      generateDartPadUrl();
    }
  }, [code]);

  /*
   * const setSelectedView = (view: WorkbenchViewType) => {
   *   workbenchStore.currentView.set(view);
   * };
   * useEffect(() => {
   *   if (hasPreview) {
   *     setSelectedView('preview');
   *   }
   * }, [hasPreview]);
   */
  const handleErrorModalClose = useCallback(() => {
    setShowErrorModal(false);
  }, [navigate]);

  const handleRetry = useCallback(() => {
    setShowErrorModal(false);
    navigate('/');
  }, [navigate]);

  useEffect(() => {
    // console.log('all files from filestore', files);

    workbenchStore.setDocuments(files);
  }, [files]);

  const onEditorChange = useCallback<OnEditorChange>((update) => {
    workbenchStore.setCurrentDocumentContent(update.content);
  }, []);

  const onEditorScroll = useCallback<OnEditorScroll>((position) => {
    workbenchStore.setCurrentDocumentScrollPosition(position);
  }, []);

  const onFileSelect = useCallback((filePath: string | undefined) => {
    workbenchStore.setSelectedFile(filePath);
  }, []);

  const onFileSave = useCallback(() => {
    workbenchStore.saveCurrentDocument(fileActions, storeFileActions).catch(() => {
      Sentry.captureException(new Error('Failed to save file'), {
        extra: {
          message: 'Error saving file',
          fileActions,
          storeFileActions,
        },
      });
      toast.error('Failed to update file content');
    });
  }, [fileActions, storeFileActions]);

  const onFileReset = useCallback(() => {
    workbenchStore.resetCurrentDocument();
  }, []);

  const confirmDownload = useCallback(() => {
    console.log('Download confirmed - starting download process');

    try {
      workbenchStore.downloadZip();
      console.log('Download initiated successfully');
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Error during downloading zip',
        },
      });
      console.error('Download failed:', error);
    }
    setShowDownloadModal(false);
  }, []);

  const onDownloadClick = useCallback(() => {
    console.log('Download button clicked - opening modal');
    confirmDownload();
  }, []);

  return (
    <>
      {chatStarted && (
        <motion.div initial="closed" animate={showWorkbench ? 'open' : 'closed'} variants={workbenchVariants}>
          {!isFlutter ? (
            <div
              className={classNames(
                'fixed top-[calc(var(--header-height)+1.5rem)] bottom-6 mr-4 z-0 transition-[right,left,width] duration-200 alpha-ease-cubic-bezier',
                {
                  'right-[calc(var(--workbench-left)-0.5rem)]': showWorkbench,
                  'top-[100%]': !showWorkbench,
                  'w-[var(--workbench-inner-width)]': deviceView === 'desktop' || selectedView === 'code',
                  'w-[430px]': deviceView === 'mobile' && selectedView === 'preview',
                  'w-[860px]': deviceView === 'tablet' && selectedView === 'preview',
                },
              )}
            >
              <div className="absolute inset-0 px-6">
                <div className="relative h-full flex flex-col bg-alpha-elements-background-depth-2 border border-alpha-elements-borderColor shadow-sm rounded-lg overflow-hidden">
                  {/* <div className=" flex mx-auto items-center px-3 py-2 border-b border-alpha-elements-borderColor">
                <Slider selected={selectedView} options={sliderOptions} setSelected={setSelectedView} />

                {selectedView === 'code' && (
                  <PanelHeaderButton
                    className="mx-2 text-sm absolute right-0"
                    onClick={() => {
                      workbenchStore.toggleTerminal(!workbenchStore.showTerminal.get());
                    }}
                  >
                    <div className="i-ph:terminal" />
                    Terminal
                  </PanelHeaderButton>

                )}
              </div> */}

                  <div className="relative flex-1 overflow-hidden">
                    {/* {selectedView === 'code' && (
                      <div
                        className="flex items-center gap-2 p-2 cursor-pointer hover:bg-gray-100 rounded-md transition-colors"
                        onClick={onDownloadClick}
                      >
                        <div className="i-ph:code text-xl" />
                        <span className="text-sm font-medium">Download Code</span>
                      </div>
                    )} */}
                    <View
                      initial={{ x: selectedView === 'code' ? 0 : '-100%' }}
                      animate={{ x: selectedView === 'code' ? 0 : '-100%' }}
                    >
                      <EditorPanel
                        editorDocument={currentDocument}
                        isStreaming={isStreaming}
                        selectedFile={selectedFile}
                        files={files}
                        unsavedFiles={unsavedFiles}
                        onFileSelect={onFileSelect}
                        onEditorScroll={onEditorScroll}
                        onEditorChange={onEditorChange}
                        onFileSave={onFileSave}
                        onFileReset={onFileReset}
                        onDownloadClick={onDownloadClick}
                      />
                    </View>
                    <View
                      initial={{ x: selectedView === 'preview' ? 0 : '100%' }}
                      animate={{ x: selectedView === 'preview' ? 0 : '100%' }}
                    >
                      <Preview isStreaming={isStreaming} />
                    </View>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="relative w-full">
              {dartPadisLoading ? (
                <div className="flex flex-row items-center justify-center w-[75vw] min-h-[80vh]">
                  <div className="flex items-center gap-4">
                    <div className="w-8 h-8 border-3 border-t-blue-500 border-blue-200 rounded-full animate-spin"></div>
                    <span className="text-gray-700 text-lg">Loading Flutter Editor...</span>
                  </div>
                </div>
              ) : (
                <iframe
                  style={{
                    width: isFlutter ? '96%' : '100%',
                    height: '80vh',
                    border: 0,
                    zIndex: 100,
                    marginTop: 'calc(var(--header-height) )',
                    right: showWorkbench ? 'calc(var(--workbench-left) - 0.5rem)' : '100%',
                  }}
                  src={url}
                />
              )}
            </div>
          )}
        </motion.div>
      )}

      <ErrorModal
        isOpen={showErrorModal}
        onClose={handleErrorModalClose}
        onRetry={handleRetry}
        title="Flutter Editor Error"
        message="Flutter Editor could not be loaded. Please try again later or contact support if the issue persists." // change this message a bit
      />
    </>
  );
});

interface ViewProps extends HTMLMotionProps<'div'> {
  children: JSX.Element;
}

const View = memo(({ children, ...props }: ViewProps) => {
  return (
    <motion.div className="absolute inset-0" transition={viewTransition} {...props}>
      {children}
    </motion.div>
  );
});
